import { useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  onLoadMore: (() => void) | undefined;
  hasMore: boolean | undefined;
  isLoadingMore: boolean | undefined;
  rootMargin?: string;
}

export function useInfiniteScroll<T extends Element>({
  onLoadMore,
  hasMore,
  isLoadingMore,
  rootMargin = '120px',
}: UseInfiniteScrollOptions) {
  const sentinelRef = useRef<T | null>(null);

  useEffect(() => {
    if (!onLoadMore || !hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      { root: null, rootMargin, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isLoadingMore, rootMargin]);

  return sentinelRef;
}
